import ArrowLong from '@assets/svg/ArrowLong';
import { cn } from '@my-medication/shared-components/shadcn/utils';
import { Notification } from '@my-medication/shared-components/shared/Notification';
import { Button } from '@my-medication/shared-components/ui/Button';
import { Spinner } from '@my-medication/shared-components/ui/Spinner';
import { IDS } from '@my-medication/e2e-page-objects/selectors';

import { Pagination } from '../Pagination';
import { type SearchResultsListProps } from './SearchResultsList.types';

const SearchResultsList = <T,>({
  value,
  columns,
  rowKey,
  noResultsMessage,
  hasSearched = false,
  errorMessage,
  loadingMessage,
  loading = false,
  showDetailButton = true,
  paginationIndexBase = 1,
  onRowClick,
  onPageChange,
}: SearchResultsListProps<T>) => {
  const items = value?.items ?? [];
  const pageCount = value?.pageCount ?? 0;
  const currentPage = value?.currentPage ?? paginationIndexBase;

  const displayCurrentPage = paginationIndexBase === 0 ? currentPage + 1 : currentPage;

  const handlePageChange = (newDisplayPage: number) => {
    onPageChange?.(paginationIndexBase === 0 ? newDisplayPage - 1 : newDisplayPage);
  };

  return (
    <div className="flex flex-col">
      {items.length > 0 && pageCount > 1 && (
        <Pagination
          position="top"
          dataTestid={IDS.paginationRoot('top')}
          pageCount={pageCount}
          currentPage={displayCurrentPage}
          loading={loading}
          onPageChange={handlePageChange}
        />
      )}

      {loadingMessage && (
        <div data-testid={IDS.searchResultsLoading} className="flex w-full flex-col items-center px-4 py-4 text-center">
          <Spinner size="lg" />
          <p className="text-body-1 text-secondary-900">{loadingMessage}</p>
        </div>
      )}

      {items.length > 0 ? (
        <div className="mt-12.5 w-full max-w-full overflow-x-auto" tabIndex={0}>
          <table className="w-full min-w-max">
            <thead>
              <tr className="border-b-3 border-secondary-900">
                {columns.map((column) => (
                  <th key={column.key} className="p-2.5 text-left whitespace-nowrap">
                    {column.header}
                  </th>
                ))}
                {showDetailButton && <td className="p-2.5 text-left" />}
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr
                  key={rowKey(item, index)}
                  className={cn('group border-b border-secondary-200 bg-secondary-050 hover:bg-white', {
                    'cursor-pointer': !!onRowClick,
                  })}
                  onClick={() => onRowClick?.(item)}
                >
                  {columns.map((column) => (
                    <td key={column.key} className="p-2.5">
                      {column.render(item)}
                    </td>
                  ))}
                  {showDetailButton && (
                    <td className="p-2.5">
                      <Button
                        type="button"
                        name={`search-results-detail-${rowKey(item, index)}`}
                        variant="outline"
                        size="icon"
                        tabIndex={-1}
                        aria-hidden="true"
                        className="size-7.5! rounded-full! border-secondary-900 bg-white! group-hover:bg-secondary-900!"
                        startIcon={<ArrowLong className="size-4! **:fill-secondary-900 group-hover:**:fill-white" />}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        hasSearched && (
          <div className="mt-4 flex flex-col gap-4">
            {errorMessage && (
              <Notification dataTestid={IDS.searchResultsError} variant="error" description={errorMessage} />
            )}
            {!errorMessage && (
              <Notification dataTestid={IDS.searchResultsEmpty} variant="warning" title={noResultsMessage} />
            )}
          </div>
        )
      )}

      {items.length > 4 && pageCount > 1 && (
        <Pagination
          dataTestid={IDS.paginationRoot('bottom')}
          pageCount={pageCount}
          currentPage={displayCurrentPage}
          loading={loading}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

SearchResultsList.displayName = 'SearchResultsList';

export { SearchResultsList };
